import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Select from "../../../Common/FormElements/Select/Select";
import TextArea from "../../../Common/FormElements/TextArea/TextArea";
import TextInput from "../../../Common/FormElements/TextInput/TextInput";
import CalendarInput from "../../../Common/FormElements/CalendarInput/CalendarInput";
import Button from "../../../Common/FormElements/Button/Button";

import { actionsForm } from "../data";
import { createUpdateAction } from "../../../../REDUX/actions/prospects";
import {
  getActionFormTitle,
  getInterlocutorOptions,
  getProspectActionTypeOptions,
  getValidTypeForActionStatus,
  initialActionFormData,
  isActionFormValid,
} from "../../../../Utils/prospect";
import { useScrollIntoView } from "../../../../hooks/useScroll";

const ActionForm = ({ status, action, data, handleClose }) => {
  const UPDATE = Boolean(data);
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const { interlocutors } = useSelector((state) => state?.interlocutorReducer);
  const { ref } = useScrollIntoView();
  const [formData, setFormData] = useState(initialActionFormData(data, t));

  useEffect(() => {
    setFormData((prev) => {
      return {
        ...prev,
        type: getValidTypeForActionStatus(status, prev?.type?.code, t),
      };
    });
  }, [status, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    dispatch(
      createUpdateAction({ ...formData }, action === "REMINDER", status, t)
    );
    handleClose();
  };

  const getOptions = (name, options) => {
    if (name === "type") {
      return getProspectActionTypeOptions(status, action, t);
    }

    if (name === "interlocutor") {
      return getInterlocutorOptions(interlocutors, t);
    }

    return (options ?? []).map((opt) => ({
      name: t(opt.name),
      code: opt.code,
    }));
  };

  const renderField = (field) => {
    const commonProps = {
      key: field.name,
      className: field.type === "textarea" ? "col-12" : "col-6",
    };

    if (field.type === "select") {
      return (
        <div {...commonProps}>
          <Select
            {...field}
            label={field.label ? `${t(field.label)}` : ""}
            placeholder={field.placeholder ? t(field.placeholder) : ""}
            value={formData[field.name]}
            options={getOptions(field.name, field.options)}
            callback={handleChange}
            showClear
            resetable
            appendToSelf
          />
        </div>
      );
    }

    if (field.name === "date") {
      const placeholder =
        action === "ACTION" ? "Date Action Completed" : "Reminder Date";
      return (
        <div {...commonProps}>
          <CalendarInput
            {...field}
            placeholder={t(placeholder)}
            value={formData.date}
            callback={(e) => handleChange(e, field.name)}
            disabled={field.disabled}
            minDate={false}
            className="w100"
          />
        </div>
      );
    }

    if (field.type === "textarea") {
      return (
        <div {...commonProps}>
          <TextArea
            {...field}
            placeholder={field.placeholder ? t(field.placeholder) : ""}
            value={formData.comment ?? ""}
            callback={handleChange}
          />
        </div>
      );
    }

    return (
      <div {...commonProps}>
        <TextInput
          {...field}
          label={field.label ? `${t(field.label)}` : ""}
          placeholder={field.placeholder ? t(field.placeholder) : ""}
          value={formData[field.name]}
          callback={handleChange}
          disabled={field.disabled}
        />
      </div>
    );
  };

  return (
    <div
      className={`${!UPDATE ? "p-4 bg-dimGrey" : ""} my-5 position-relative`}
      ref={ref}
    >
      <p className="text-uppercase text-dark mb-4">
        {getActionFormTitle(action, UPDATE, t)}
      </p>

      <div className="row g-4">{actionsForm.map(renderField)}</div>

      <div className="w-100 d-flex justify-content-center mt-4">
        <Button
          onClick={handleSubmit}
          disabled={!isActionFormValid(action, formData)}
        >
          {t("Validate")}
        </Button>
      </div>

      {!UPDATE && (
        <button
          className="border-0 bg-transparent px-1 text-dark position-absolute top-0 end-0 f24"
          onClick={handleClose}
        >
          <i className="bi bi-x" />
        </button>
      )}
    </div>
  );
};

export default ActionForm;
